/**
 * The session PHASE - what the interface calls the "mode": Narrative,
 * Stealth or Combat. Set by the GM for the whole table.
 *
 * It is `phase` in code because "mode" is already the map's per-viewer
 * Play / Build / Deploy.
 */

/** In the order the header's plate shows them. */
import type { StealthState } from '../stealth/ladder';
import type { DecisionTimer } from '../narrative/timer';

export const PHASES = ['narrative', 'stealth', 'combat'] as const;

export type Phase = (typeof PHASES)[number];

/** `direction/frame` - what the GM directs. Read by every member, written by the GM alone. */
export interface PhaseFrame {
  phase: Phase;
  /** The team tag of the party. Everyone else still in the fight is the opposition. Null until the GM names it. */
  partyTeam: string | null;
  /** The danger step the GM has pinned (1 to 4), or null to follow the numbers - see `danger.ts`. */
  dangerPin: 1 | 2 | 3 | 4 | null;
  /**
   * Do positions on the map count for the rules? `null` follows the phase
   * (`PHASE_LAYOUT.positionsDefault`); the GM may say otherwise either way.
   * A fact about the TABLE: looking at the map is each viewer's own business
   * and changes nothing about how an attack resolves.
   */
  positions: boolean | null;
  /**
   * How many beats of time have passed outside the rounds (`time passes`).
   * A count that only grows: whatever ticks by the round on each screen
   * (a weapon's recharge) ticks by this as well.
   */
  beats: number;
  /**
   * Stealth's ladder: the rung, its line, who among the opposition is doing
   * what, and what the party has learned (`engine/stealth/ladder`). OPTIONAL
   * AND NEVER DEFAULTED: a table that has never used Stealth stores none,
   * and `stealthOf` reads the empty ladder. On the frame so that it is the
   * GM's to write, waits to be read with the phase, and is restored by undo
   * with it.
   */
  stealth?: StealthState;
  /**
   * The decision timer, while one is up (`engine/narrative/timer`). OPTIONAL:
   * absent means none. Null in a patch clears it.
   */
  timer?: DecisionTimer | null;
}
